import React, { Component } from "react";
import FileSaver from "file-saver";
import XLSX from "xlsx";
import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button, Dropdown, Menu, Spin } from "antd";
import { achFilesNames } from "../utils/commonStringsConsts";
import { getRegion } from "./regionWrapper";
import { Region } from "../utils/commonInterfaces";

const fileType =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
const fileExtension = ".xlsx";
interface Props {
  apiData?: any;
  fileName: string;
  otherSheets?: any[];
  buttonName?: string,
  buttonColor?: string,
  multipleFiles?: boolean,
  generateData?: any,
  loadingDataSheets?: boolean,
}

export const exportToCSV = async (apiData: unknown[], fileName: string, generateData?: any, otherSheets?: any[]) => {
  const region = getRegion() as Region;
  if (generateData) {
    apiData = await generateData()
  }

  const allFiles = [];
  if (Array.isArray(apiData) && apiData.length > 0) allFiles.push({
    name: fileName,
    data: apiData,
  })

  if (otherSheets) {
    for (let index = 0; index < otherSheets.length; index++) {
      if (otherSheets[index].onClick) {
        otherSheets[index].data = await otherSheets[index].onClick()
      }
      allFiles.push(otherSheets[index]);
    }
  }
  if (region === Region.EG) {
    for (let i = 0; i < allFiles.length; i++) {
      const worksheet = XLSX.utils.json_to_sheet(allFiles[i].data);
      const max_width = allFiles[i]?.data?.reduce((acc: number[], row: { [key: string]: any }) => {
        Object.keys(row).forEach((key, index) => {
          const value = row[key] ? row[key].toString().length : 10;
          acc[index] = Math.max(acc[index] || 10, value);
        });
        return acc;
      }, []);
      worksheet['!cols'] = max_width?.map((w: number) => ({ wch: w }));
      let sheets: { [key: string]: XLSX.WorkSheet } = {
        data: worksheet,
      };
      if (fileName === achFilesNames.HSBC) {
        const ksaData = [{
          'Account Title': 'Nowpay',
          'Debit Account': 'SA 0000000000000000000000',
          'Value Date (YYYYMMDD)': new Date().toISOString().slice(0, 10).replace(/-/g, ''),
          'Total Transactions': apiData.length,
          'Total Amount': 1000000, //Sum of all amounts
          'Narrative': '', //Wait to PM confirm
          'File Name': `${'Batch'} ${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`,
          'File Location': '',
          'Payment set': 'FCB', //Wait to PM confirm
        }];
        sheets.additionalSheet = XLSX.utils.json_to_sheet(ksaData || []); // Use existing additional data if available
      }
      let names = ["data"];
      if (fileName === 'Processing HSBC Bank Requests list') {
        names.push("additionalSheet");
      }
      const wb = { Sheets: sheets, SheetNames: names };
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: fileType });
      allFiles.length > 1 ?
        FileSaver.saveAs(data, allFiles[i].statusName + fileExtension) :
        FileSaver.saveAs(data, allFiles[i].name + fileExtension)
    }
  } else if (region === Region.SE) {
    const numbersFormatKeys = [
      "Due this Month",
      "Due principal this Month",
      "Due fees this Month",
      "User Collections",
      "Previous Dues After Collections",
      "Total due this Month",
      "Total Outstanding",
      "Outstanding Amount",
      "Outstanding Principal",
      "Outstanding Fees",
      "outstandingAmount",
      "remainingInstallments",
    ];
    const numbersToStrings = [
      //fix numbers to strings
      "userID",
      "Remaining Payment Cycle",
      "installmentID",
      "paymentCycle",
      "AdvanceID",
      "nationalID",
      "numberOfMonths",
      "serviceTypeId",
      "companyID"
    ]
    for (let i = 0; i < allFiles.length; i++) {
      const worksheet = XLSX.utils.json_to_sheet(allFiles[i].data);
      const headers = Object.keys(allFiles[i]?.data[0] || {});
  
      const numberFormatIndices = numbersFormatKeys
        .map(key => headers.indexOf(key))
        .filter(index => index !== -1);
  
      const stringFormatIndices = numbersToStrings
        .map(key => headers.indexOf(key))
        .filter(index => index !== -1);
  
      Object.keys(worksheet).forEach(cellRef => {
        if (cellRef[0] === '!') return;
  
        const cell = worksheet[cellRef];
        const value = cell.v;
  
        const { r, c } = XLSX.utils.decode_cell(cellRef);
  
        if (r === 0) return;
  
        if (stringFormatIndices.includes(c)) {
          cell.t = "s";     // set type to string
          cell.v = String(value ?? "");
          cell.z = undefined; // remove formatting
          return;
        }
  
        if (numberFormatIndices.includes(c) || (!isNaN(value) && value !== "" && value !== null && cell.t === 'n')) {
          cell.t = 'n';
          cell.v = Number(value);
          if (numberFormatIndices.includes(c)) {
            cell.z = "#,##0.00";
          } else {
            cell.z = "0";
          }
        }
      });
  
      const max_width = allFiles[i]?.data?.reduce((acc: number[], row: { [key: string]: any }) => {
        Object.keys(row)?.forEach((key, index) => {
          const value = row[key] ? row[key].toString().length : 10;
          acc[index] = Math.max(acc[index] || 10, value);
        });
        return acc;
      }, []);
      worksheet['!cols'] = max_width?.map((w: number) => ({ wch: w }));
  
      const textFormatKeys = [
        "REFERENCE",
        "DEBIT_ACCOUNT",
        "BENEFICIARY_ZIP_CODE",
        "BENEFICIARY_BANK_ZIP_CODE",
        "TRANSACTION_AMOUNT",
        "TRANSACTION_DATE",
        "BENEFICIARY_CORP_ID_CODE",
        "BENEFICIARY_THIRD_PARTY_TYPE",
        "BENEFICIARY_THIRD_PARTY_VALUE",
      ];
  
      if (allFiles[i]?.data?.length > 0) {
        const headers = Object.keys(allFiles[i]?.data[0]);
  
        const textFormatIndices = textFormatKeys.map(key => headers.indexOf(key)).filter(index => index !== -1);
  
        if (textFormatIndices.length > 0) {
          for (let rowIndex = 0; rowIndex < allFiles[i]?.data?.length; rowIndex++) {
            textFormatIndices.forEach(colIndex => {
              const colLetter = XLSX.utils.encode_col(colIndex);
              const cellRef = colLetter + (rowIndex + 2); // +2 because row 1 is headers
  
              if (worksheet[cellRef]) {
                worksheet[cellRef].t = 's'; // 's' means string/text format
  
                if (!worksheet[cellRef].z) {
                  worksheet[cellRef].z = '@'; // '@' is Excel's text format
                }
              }
            });
          }
        }
      }
  
      let sheets: { [key: string]: XLSX.WorkSheet } = {
        data: worksheet,
      };
  
      const wb = { Sheets: sheets, SheetNames: ["data"] };
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: fileType });
      allFiles.length > 1 ?
        FileSaver.saveAs(data, allFiles[i].statusName + fileExtension) :
        FileSaver.saveAs(data, allFiles[i].name + fileExtension)
    }
   }
};

class ExportToExcel extends Component<Props> {
  state = {};
  render() {
    const menu = (
      <Menu
        style={{ minWidth: 220 }}
        selectable={false}
      >
        <Menu.Item
          icon={<DownloadOutlined />}
          onClick={(e) => exportToCSV(
            this.props.apiData,
            this.props.fileName,
            this.props.generateData,
            this.props.otherSheets,
          )}
          style={{ padding: "8px 12px" }}
        >
          Download all sheets
        </Menu.Item>
        {this.props.apiData && (
          <Menu.Item
            key="main"
            icon={<DownloadOutlined />}
            onClick={(e) => exportToCSV(this.props.apiData, this.props.fileName, this.props.generateData, this.props.otherSheets)}
            style={{ padding: "8px 12px" }}
          >
            {this.props.fileName}
          </Menu.Item>
        )}
        {this.props.otherSheets && this.props.otherSheets.length > 0 && (
          <Menu.Divider style={{ margin: "4px 0" }} />
        )}
        {this.props.otherSheets?.map((sheet, i) => (
          <Menu.Item
            key={i}
            icon={<DownloadOutlined style={{ fontSize: 12 }} />}
            onClick={async (e) => {
              if (sheet.onClick) {
                const data = await sheet.onClick()
                sheet.data = data
              }
              exportToCSV(sheet.data, sheet.statusName, this.props.generateData)
            }}
            style={{ padding: "6px 12px", fontSize: 13 }}
          >
            {sheet.name}
          </Menu.Item>
        ))}
      </Menu>
    );

    const triggerStyle: React.CSSProperties = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      padding: this.props.loadingDataSheets ? "3px 12px" : "6px 12px",
      minHeight: 32,
      borderRadius: 6,
      border: "1px solid #d9d9d9",
      background: "#fff",
      cursor: this.props.loadingDataSheets ? "not-allowed" : "pointer",
      opacity: this.props.loadingDataSheets ? 0.7 : 1,
      transition: "border-color 0.2s, box-shadow 0.2s",
      marginTop: "17px",
    };

    return (
      <>
        {!this.props.otherSheets ? (

          <>
            {this.props.multipleFiles ?
              <Button type="default" onClick={() => exportToCSV(this.props.apiData, this.props.fileName, this.props.generateData)}
                style={{ color: this.props.buttonColor, borderColor: this.props.buttonColor, margin: '0 19px' }}>
                {this.props.buttonName}
              </Button>
              : <DownloadOutlined
                onClick={(e) =>
                  exportToCSV(this.props.apiData, this.props.fileName, this.props.generateData)
                }
                style={{ float: "right", margin: "20px 0 0 0", fontSize: "25px" }}
              />}
          </>
        ) : (
            <Dropdown overlay={menu} disabled={this.props.loadingDataSheets} trigger={["click"]}>
            <span
                style={triggerStyle}
            >
                <Spin spinning={this.props.loadingDataSheets? true : false} indicator={<LoadingOutlined spin style={{ color: "black" }} />} size="small" />
                <DownloadOutlined style={{ fontSize: 18 }} />
              </span>
            </Dropdown>
        )}

      </>
    );
  }
}

export default ExportToExcel;